import { useEffect } from "react";
import * as Sentry from "@sentry/react";

import { AppRouter } from "@/common/components/router/AppRouter";
import { ApiProvider } from "@/api/ApiProvider";
import { QueryProvider } from "@/common/providers/QueryProvider";
import { initGTM } from "@/common/utils/analytics";
import { AuthProvider } from "@/domain/auth/AuthProvider";
import { SidebarProvider as SidebarConfigProvider } from "@/domain/sidebar/SidebarProvider";
import { ThemeProvider } from "@/domain/theme/ThemeProvider";
import { ThemeInitializer } from "@/domain/theme/ThemeInitializer";
import { Button } from "@/domain/ui/Button";
import { ErrorNotification } from "@/domain/ui/Notification";
import { Toaster } from "@/domain/ui/Sonner";

// Get basename from environment (for deployment) or use empty string for development
const basename = import.meta.env.VITE_BASENAME || "";

function App() {
  // Initialize GTM on app load
  useEffect(() => {
    initGTM();
  }, []);

  return (
    <div className="font-sans antialiased" style={{ fontFamily: "var(--font-inter)" }}>
      <QueryProvider>
        <ApiProvider>
          <AuthProvider>
            <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
              <ThemeInitializer />
              <SidebarConfigProvider>
                <AppRouter basename={basename} />
              </SidebarConfigProvider>
              <Toaster />
            </ThemeProvider>
          </AuthProvider>
        </ApiProvider>
      </QueryProvider>
    </div>
  );
}

const ErrorFallback = ({ error, resetError }: { error: unknown; resetError: () => void }) => {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <ErrorNotification
        title="Something went wrong"
        message="An unexpected error occurred. Please try refreshing the page."
        error={error}
        actions={<Button onClick={resetError}>Try again</Button>}
        className="w-full max-w-md"
      />
    </div>
  );
};

export default function AppWithErrorBoundary() {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}>
      <App />
    </Sentry.ErrorBoundary>
  );
}
